import axios from 'axios';
import { useAuthStore } from '../store';
import { onOwnAdminHost } from './adminHost';

/**
 * Axios API Client Configuration - Secure Production Version
 */

// Keep track of active silent refresh requests to avoid duplicate concurrent calls
let isRefreshing = false;
let failedQueue = [];

// Local variable to store transient CSRF token in memory
let csrfToken = null;

// Old builds kept the access token in localStorage; nothing reads it any more,
// so a copy left behind is only a stale credential — drop it.
try { localStorage.removeItem('token'); } catch (_) { /* storage blocked */ }

export const SESSION_EXPIRED_KEY = 'hotwifi.sessionExpired';

/**
 * The session can't be renewed (refresh token expired or revoked): forget the
 * old token and send the admin to the login page, which then says why.
 */
export const endSession = () => {
    try { localStorage.removeItem('token'); } catch (_) { /* storage blocked */ }
    try { sessionStorage.setItem(SESSION_EXPIRED_KEY, '1'); } catch (_) { /* storage blocked */ }
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/login') window.location.href = '/login';
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Determine API URL dynamically to avoid hardcoded IP issues
const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        // IP-based access → always relative (avoids CORS)
        if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
            return '/api';
        }
        // HTTP page = captive portal served to unauthenticated clients.
        // Use relative URL so API calls go to the same origin (port 80),
        // where nginx /api/portal/ and /api/public/ proxy to the backend.
        // Avoids CORS rejection (http:// origin vs https:// ALLOWED_ORIGINS).
        if (window.location.protocol === 'http:') {
            return '/api';
        }
        // A tenant's own admin subdomain, or super.hotwifi.in: this host's own API.
        if (onOwnAdminHost()) {
            return '/api';
        }
    }
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    return '/api';
};

// Initialize Axios instance with HTTP credentials enabled globally for HttpOnly cookies
const api = axios.create({
    baseURL: getBaseUrl(),
    timeout: 10000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Silent CSRF token fetcher to populate the secure cookie and retrieve memory token
 */
export const fetchCSRFToken = async () => {
    try {
        const response = await axios.get(`${getBaseUrl()}/csrf-token`, { withCredentials: true });
        csrfToken = response.data.csrfToken;
        return csrfToken;
    } catch (err) {
        console.error('❌ Failed to retrieve CSRF token:', err);
        return null;
    }
};

// Request interceptor - Add transient JWT auth token & CSRF header
api.interceptors.request.use(
    async (config) => {
        const url = config.url || '';
        const isAuthPath = url.includes('/auth/admin/login') || url.includes('/auth/refresh') || url.includes('/csrf-token');

        let token = useAuthStore.getState().token;

        // 1. Silent Refresh on startup / reload: if authenticated but memory token is empty
        if (!token && useAuthStore.getState().isAuthenticated && !isAuthPath) {
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const response = await axios.post(
                        `${getBaseUrl()}/auth/refresh`,
                        {},
                        { withCredentials: true }
                    );
                    token = response.data.data.token;
                    useAuthStore.getState().login(useAuthStore.getState().admin, token);
                    isRefreshing = false;
                    processQueue(null, token);
                } catch (refreshError) {
                    isRefreshing = false;
                    processQueue(refreshError, null);
                    // Refresh failed: forget the old token and ask to log in again
                    endSession();
                    return Promise.reject(refreshError);
                }
            } else {
                // If another request is currently refreshing the token, wait in queue
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (newToken) => {
                            config.headers.Authorization = `Bearer ${newToken}`;
                            resolve(config);
                        },
                        reject: (err) => {
                            reject(err);
                        }
                    });
                });
            }
        }

        // 2. Set Access Token inside Bearer Authorization header
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // 3. CSRF Protection
        const mutableMethod = ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() || '');

        // HTTP pages are captive portal pages served to unauthenticated guests.
        // The backend already excludes /api/portal from CSRF validation, so
        // fetching a CSRF token here is unnecessary — and causes a 301 redirect
        // on Android WebView because the relative /api/csrf-token URL hits nginx's
        // HTTP→HTTPS redirect, leaving captive portal devices stuck in a redirect loop.
        const isHttpPage = typeof window !== 'undefined' && window.location.protocol === 'http:';

        // Only skip CSRF for truly public endpoints that don't need it
        const isSkippedRoute =
            url.includes('/public/') ||
            url.includes('/health') ||
            url.includes('/status');

        // Portal authenticate explicitly gets CSRF even if other portal routes are skipped
        const isPortalAuth = url.includes('/portal/authenticate');

        if (!isHttpPage && mutableMethod && !isAuthPath && (!isSkippedRoute || isPortalAuth)) {
            if (!csrfToken) {
                await fetchCSRFToken();
            }
            if (csrfToken) {
                config.headers['x-csrf-token'] = csrfToken;
            }
        }

        // Request Log
        console.log(`🚀 Secure API Request [${config.method?.toUpperCase()}]: ${config.url}`);

        return config;
    },
    (error) => {
        console.error('❌ Request Interceptor Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor - Handle responses & Access Token expiration retries (401)
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const url = originalRequest.url || '';
        const isLoginAttempt = url.includes('/auth/admin/login');

        // Handle CSRF validation failure (403). The server always issues a fresh
        // csrf-token cookie on every GET /csrf-token call (needed to avoid a
        // cluster-mode validation bug), which means the cached in-memory
        // `csrfToken` can go stale the moment ANYTHING else re-fetches a token
        // (another tab, a background call) — the cookie moves on, the cached
        // header value doesn't. Rather than fight that, just self-heal: on a
        // CSRF 403, clear the cache, fetch a fresh token, and retry once.
        const isCsrfFailure = error.response?.status === 403
            && (error.response?.data?.message || '').toLowerCase().includes('csrf');

        if (isCsrfFailure && !originalRequest._csrfRetry) {
            originalRequest._csrfRetry = true;
            csrfToken = null;
            const freshToken = await fetchCSRFToken();
            if (freshToken) {
                originalRequest.headers['x-csrf-token'] = freshToken;
                return api(originalRequest);
            }
            return Promise.reject(error);
        }

        // Handle 401 Unauthorized (AccessToken expired in flight)
        if (error.response?.status === 401 && !originalRequest._retry && !isLoginAttempt) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const response = await axios.post(
                        `${getBaseUrl()}/auth/refresh`,
                        {},
                        { withCredentials: true }
                    );

                    const newToken = response.data.data.token;
                    useAuthStore.getState().login(useAuthStore.getState().admin, newToken);
                    isRefreshing = false;
                    processQueue(null, newToken);

                    // Re-sign request header and retry
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                } catch (refreshErr) {
                    isRefreshing = false;
                    processQueue(refreshErr, null);
                    // Refresh token is revoked or expired - terminate user session
                    endSession();
                    return Promise.reject(refreshErr);
                }
            } else {
                // If a token refresh is already pending, wait for it to resolve
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (newToken) => {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            resolve(api(originalRequest));
                        },
                        reject: (err) => {
                            reject(err);
                        }
                    });
                });
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Auth API
 */
export const authAPI = {
    login: (email, password) =>
        api.post('/auth/admin/login', { email, password }),

    login2FA: (token, tempToken) =>
        api.post('/auth/admin/login-2fa', { token, tempToken }),

    register: (data) =>
        api.post('/auth/admin/register', data),

    logout: async () => {
        try {
            await api.post('/auth/logout');
        } catch (_) {
            // Proceed to clear frontend state even if backend fails
        }
        useAuthStore.getState().logout();
        csrfToken = null;
    },
};

/**
 * User API
 */
export const userAPI = {
    requestOTP: (mobile, branchId) =>
        api.post('/users/auth/request-otp', { mobile, branchId }),

    verifyOTP: (data) =>
        api.post('/users/auth/verify-otp', data),

    macLogin: (data) =>
        api.post('/users/auth/mac-login', data),

    voucherLogin: (data) =>
        api.post('/users/auth/voucher-login', data),

    logout: (userId, sessionId) =>
        api.post('/users/logout', { userId, sessionId }),

    getAll: (branchId, params = {}) =>
        api.get(`/users`, { params: { branchId, ...params } }),

    getById: (id) =>
        api.get(`/users/${id}`),

    create: (data) =>
        api.post('/users', data),

    update: (id, data) =>
        api.put(`/users/${id}`, data),

    delete: (id) =>
        api.delete(`/users/${id}`),
};

/**
 * Tenant API
 */
export const tenantAPI = {
    getAll: (params = {}) => api.get('/tenants', { params }),
    getById: (id) => api.get(`/tenants/${id}`),
    create: (data) => api.post('/tenants', data),
    update: (id, data) => api.put(`/tenants/${id}`, data),
    delete: (id) => api.delete(`/tenants/${id}`),
};

/**
 * Branch API
 */
export const branchAPI = {
    getAll: (tenantId, params = {}) => api.get('/branches', { params: { tenantId, ...params } }),
    getById: (id) => api.get(`/branches/${id}`),
    create: (data) => api.post('/branches', data),
    update: (id, data) => api.put(`/branches/${id}`, data),
    delete: (id) => api.delete(`/branches/${id}`),
    generateApiKeys: (id) => api.post(`/branches/${id}/generate-api-keys`),
};

/**
 * Plan API
 */
export const planAPI = {
    getAll: (branchIdOrParams) => {
        const params = branchIdOrParams && typeof branchIdOrParams === 'object'
            ? branchIdOrParams
            : { branchId: branchIdOrParams };
        return api.get('/plans', { params });
    },
    getById: (id) => api.get(`/plans/${id}`),
    create: (data) => api.post('/plans', data),
    update: (id, data) => api.put(`/plans/${id}`, data),
    delete: (id) => api.delete(`/plans/${id}`),
};

/**
 * Voucher API
 */
export const voucherAPI = {
    getAll: (branchId, params = {}) => api.get('/vouchers', { params: { branchId, ...params } }),
    getById: (id) => api.get(`/vouchers/${id}`),
    generate: (data) => api.post('/vouchers/generate', data),
    getBatch: (batchId) => api.get(`/vouchers/batch/${batchId}`),
    markUsed: (id) => api.post(`/vouchers/${id}/mark-used`),
    delete: (id) => api.delete(`/vouchers/${id}`),
    exportPDF: (batchId) => api.get(`/vouchers/batch/${batchId}/export`, {
        responseType: 'blob',
    }),
};

/**
 * Session API
 */
export const sessionAPI = {
    getAll: (params) => api.get('/sessions', { params }),
    getActive: (branchId) => api.get('/sessions/active', { params: { branchId } }),
    getById: (id) => api.get(`/sessions/${id}`),
    disconnect: (sessionId) => api.post('/sessions/disconnect', { sessionId }),
    disconnectUser: (userId) => api.post('/sessions/disconnect-user', { userId }),
    updateBandwidth: (userId, uploadSpeed, downloadSpeed) =>
        api.post('/sessions/update-bandwidth', { userId, uploadSpeed, downloadSpeed }),
    cleanupOld: (params) => api.delete('/sessions/old', { params }),
    getReport: (params) => api.get('/sessions/report', { params }),
};

/**
 * Bandwidth Manager API — live speeds, a gateway's Dynamic Bandwidth Manager, speed changes
 */
export const bandwidthAPI = {
    overview: (params) => api.get('/bandwidth/overview', { params }),
    changes: (params) => api.get('/bandwidth/changes', { params }),
    // speed: { uploadKbps, downloadKbps, reason?, pinMinutes? }
    setSpeed: (sessionId, speed) => api.post(`/bandwidth/sessions/${sessionId}/speed`, speed),
    resetSpeed: (sessionId, reason) => api.post(`/bandwidth/sessions/${sessionId}/reset`, reason ? { reason } : {}),
    // settings: { enabled, downloadCapacity, uploadCapacity, reservePercent, interimSeconds } (kbps)
    updateGateway: (nasId, settings) => api.put(`/bandwidth/gateways/${nasId}`, settings),
};

/**
 * Analytics API
 */
export const analyticsAPI = {
    // Dashboard
    getDashboard: (branchId, period) =>
        api.get('/analytics/dashboard', { params: { branchId, period } }),

    // User Stats
    getUserStats: (branchId, params) =>
        api.get('/analytics/users', { params: { branchId, ...params } }),

    // Session Stats
    getSessionStats: (branchId, params) =>
        api.get('/analytics/sessions', { params: { branchId, ...params } }),

    // Data Usage
    getDataUsage: (branchId, period) =>
        api.get('/analytics/data-usage', { params: { branchId, period } }),

    // Revenue Analytics
    getRevenue: (branchId, period) =>
        api.get('/analytics/revenue', { params: { branchId, period } }),

    // Top Plans
    getTopPlans: (branchId, limit = 5) =>
        api.get('/analytics/top-plans', { params: { branchId, limit } }),

    // Hourly Activity
    getHourlyActivity: (branchId, date) =>
        api.get('/analytics/hourly-activity', { params: { branchId, date } }),

    // Device Statistics
    getDeviceStats: (branchId, period) =>
        api.get('/analytics/device-stats', { params: { branchId, period } }),

    // Network Health
    getNetworkHealth: (branchId) =>
        api.get('/analytics/network-health', { params: { branchId } }),

    // Interface Traffic — historical upload/download per NAS
    getInterfaceTraffic: (params) =>
        api.get('/analytics/interface-traffic', { params }),

    // Average traffic over time — period: 24h | 7d | 30d | 6m | 1y; gateway: an interface key
    getTrafficGraph: (params) =>
        api.get('/analytics/traffic-graph', { params }),

    // Recent Logins
    getRecentLogins: (params) =>
        api.get('/analytics/recent-logins', { params }),

    // Force Authorize
    forceAuthorize: (attemptId, data) =>
        api.post(`/analytics/force-authorize/${attemptId}`, data),

    // Export Data
    export: (branchId, type, params) =>
        api.get(`/analytics/export/${type}`, {
            params: { branchId, ...params },
            responseType: 'blob',
        }),

    // Live Stats (Real-time)
    getLiveStats: (branchId) =>
        api.get('/analytics/live-stats', { params: { branchId } }),
};

/**
 * NAS (Network Access Server) API
 */
export const nasAPI = {
    getAll: (params) => api.get('/nas', { params }),
    // Who is online now on each gateway — MikroTik live from the router, others from accounting
    // (tenantId: the superadmin's pick; others always see their own)
    getOnline: ({ branchId, tenantId, fresh } = {}) => api.get('/nas/online', {
        params: { ...(branchId ? { branchId } : {}), ...(tenantId ? { tenantId } : {}), ...(fresh ? { fresh: 1 } : {}) },
    }),
    getById: (id) => api.get(`/nas/${id}`),
    create: (data) => api.post('/nas', data),
    update: (id, data) => api.put(`/nas/${id}`, data),
    // endSessions: also end the sessions still marked live on it (an offline router never closes them)
    delete: (id, { endSessions = false } = {}) => api.delete(`/nas/${id}`, { params: endSessions ? { endSessions: true } : undefined }),
    getStats: (id) => api.get(`/nas/${id}/stats`),
    test: (id) => api.post(`/nas/${id}/test`),
    sync: () => api.post('/nas/sync-radius'),
    discoverDevices: (id) => api.get(`/nas/${id}/discover-devices`),
    getMonitor: (id) => api.get(`/nas/${id}/monitor`),
    getLiveTraffic: (id) => api.get(`/nas/${id}/live-traffic`),
    parseConfig: (content) => api.post('/nas/parse-config', { content }),
    ruijieAuthenticate: (data) => api.post('/nas/ruijie/authenticate', data),
    ruijieNetworkGroups: (accessToken, baseUrl) => api.get('/nas/ruijie/network-groups', { params: { accessToken, baseUrl } }),
    ruijieUserGroups: (accessToken, baseUrl, networkGroupId) => api.get('/nas/ruijie/user-groups', { params: { accessToken, baseUrl, networkGroupId } }),
};

// Keep routerAPI for backward compatibility (deprecated)
export const routerAPI = nasAPI;

/**
 * Campaign API
 */
export const campaignAPI = {
    getAll: (params) => api.get('/campaigns', { params }),
    getById: (id) => api.get(`/campaigns/${id}`),
    create: (data) => api.post('/campaigns', data),
    update: (id, data) => api.put(`/campaigns/${id}`, data),
    delete: (id) => api.delete(`/campaigns/${id}`),
    getStats: (id) => api.get(`/campaigns/${id}/stats`),
    validate: (code) => api.post('/campaigns/validate', { code }),
};

/**
 * Admin Management API
 */
export const adminAPI = {
    getAll: (params) => api.get('/admins', { params }),
    getById: (id) => api.get(`/admins/${id}`),
    create: (data) => api.post('/admins', data),
    update: (id, data) => api.put(`/admins/${id}`, data),
    delete: (id) => api.delete(`/admins/${id}`),
    assignBranches: (id, branchIds) => api.put(`/admins/${id}/branches`, { branchIds }),
};

/**
 * RADIUS API
 */
export const radiusAPI = {
    disconnect: (userId) => api.post('/radius/disconnect', { userId }),
    checkUser: (username) => api.get(`/radius/check/${username}`),
    getStats: (params) => api.get('/radius/stats', { params }),
};

/**
 * Portal (Captive Portal) API
 */
export const portalAPI = {
    getLanding: (params) => api.get('/portal/landing', { params }),
    authenticate: (data) => api.post('/portal/authenticate', data),
    logoutOldSession: (data) => api.post('/portal/logout-old-session', data),
    getSessionStatus: (sessionId) => api.get(`/portal/session/${sessionId}`),
    logout: (sessionId) => api.post('/portal/logout', { sessionId }),
    sendOTP: (data) => api.post('/portal/send-otp', data),
    verifyOTP: (data) => api.post('/portal/verify-otp', data),
    // ISP plan purchase for the OTP-verified number — priced on the server.
    createIspOrder: (data) => api.post('/portal/isp/order', data),
    // A few minutes of internet before sign-in, just before paying (branch setting).
    preLoginForPayment: (data) => api.post('/portal/isp/pre-login', data),
    // Nomadix: sign the authenticated guest in at the gateway (XML RADIUS_LOGIN)
    nomadixLogin: (data) => api.post('/portal/nomadix/login', data),
    // My Wi-Fi: this browser's own connection, by the key it got at sign-in
    myWifi: (token) => api.post('/portal/my-wifi', { token }),
    myWifiDisconnect: (token) => api.post('/portal/my-wifi/disconnect', { token }),
};

/**
 * Device Management API
 */
export const deviceAPI = {
    getAll: (params) => api.get('/devices', { params }),
    create: (data) => api.post('/devices', data),
    update: (id, data) => api.put(`/devices/${id}`, data),
    delete: (id) => api.delete(`/devices/${id}`),
    getSuggestions: (params) => api.get('/devices/suggestions', { params }),
    update: (id, data) => api.put(`/devices/${id}`, data),
    bulkImport: (data) => api.post('/devices/bulk-import', data),
    bulkDelete: (ids) => api.post('/devices/bulk-delete', { ids }),
    bulkAction: (data) => api.post('/devices/bulk-action', data),
    deleteAll: (params) => api.delete('/devices/all', { params }),
    disconnect: (id) => api.post(`/devices/${id}/disconnect`),
    syncRadius: (id) => api.post(`/devices/${id}/sync-radius`),
    getStats: (params) => api.get('/devices/stats', { params }),
};

/**
 * Lead Management API
 */
export const leadAPI = {
    getAll: (params) => api.get('/leads', { params }),
    export: (params) => api.get('/leads/export', { params, responseType: 'blob' }),
};

/**
 * Template Management API (Splash Page Templates)  
 */
export const templateAPI = {
    getAll: (params) => api.get('/templates', { params }),
    getById: (id) => api.get(`/templates/${id}`),
    create: (data) => api.post('/templates', data),
    update: (id, data) => api.put(`/templates/${id}`, data),
    delete: (id) => api.delete(`/templates/${id}`),
};

/**
 * Visual Landing Page Builder API — additive, separate from templateAPI
 * (the legacy fixed-slot splash-page system above).
 */
export const landingPageAPI = {
    getAll: (params) => api.get('/landing-pages', { params }),
    getById: (id) => api.get(`/landing-pages/${id}`),
    create: (data) => api.post('/landing-pages', data),
    update: (id, data) => api.put(`/landing-pages/${id}`, data),
    delete: (id) => api.delete(`/landing-pages/${id}`),
    duplicate: (id) => api.post(`/landing-pages/${id}/duplicate`),
    publish: (id) => api.post(`/landing-pages/${id}/publish`),
    unpublish: (id) => api.post(`/landing-pages/${id}/unpublish`),
    preview: (id) => api.get(`/landing-pages/${id}/preview`),
    getRevisions: (id) => api.get(`/landing-pages/${id}/revisions`),
    restoreRevision: (id, revisionId) => api.post(`/landing-pages/${id}/revisions/${revisionId}/restore`),
    setDefault: (id, branchId) => api.post(`/landing-pages/${id}/set-default`, { branchId }),
    // The branch stops using the page; its guests get the classic portal page again.
    unsetDefault: (id, branchId) => api.post(`/landing-pages/${id}/unset-default`, { branchId }),
    saveAsTemplate: (id, data) => api.post(`/landing-pages/${id}/save-as-template`, data),
};

/**
 * Landing Page template library — shared, global LandingPage docs
 * (isTemplate:true). Superadmin-only to create (via landingPageAPI.saveAsTemplate);
 * any admin with landing_pages:view can browse/use.
 */
export const landingTemplateAPI = {
    getAll: (params) => api.get('/landing-templates', { params }),
    getById: (id) => api.get(`/landing-templates/${id}`),
    use: (id, data) => api.post(`/landing-templates/${id}/use`, data),
};

/**
 * Room Management API (Hotel/Branch Rooms)
 */
export const roomAPI = {
    getAll: (params) => api.get('/rooms', { params }),
    getById: (id) => api.get(`/rooms/${id}`),
    getStats: (branchId) => api.get(`/rooms/stats/${branchId}`),
    create: (data) => api.post('/rooms', data),
    bulkImport: (data) => api.post('/rooms/bulk-import', data),
    update: (id, data) => api.put(`/rooms/${id}`, data),
    delete: (id) => api.delete(`/rooms/${id}`),
    checkIn: (id, data) => api.post(`/rooms/${id}/check-in`, data),
    updateGuest: (id, data) => api.put(`/rooms/${id}/guest`, data),
    checkOut: (id, data) => api.post(`/rooms/${id}/check-out`, data),
    extendStay: (id, data) => api.post(`/rooms/${id}/extend-stay`, data),
    syncPMS: (branchId) => api.post(`/rooms/sync-pms/${branchId}`),
    searchGuests: (params) => api.get('/rooms/guest-search', { params }),
    getAuditLog: (id) => api.get(`/rooms/${id}/audit-log`),
};

/**
 * Branch Pages API — custom per-branch WiFi-management pages (Staff/Banquet/
 * PreCheckin/restaurant/etc), alongside Rooms.
 */
export const branchPageAPI = {
    getAll: (branchId) => api.get('/branch-pages', { params: { branchId } }),
    create: (data) => api.post('/branch-pages', data),
    update: (id, data) => api.put(`/branch-pages/${id}`, data),
    delete: (id) => api.delete(`/branch-pages/${id}`),
    getEntries: (pageId) => api.get(`/branch-pages/${pageId}/entries`),
    createEntry: (pageId, data) => api.post(`/branch-pages/${pageId}/entries`, data),
    deleteEntry: (entryId) => api.delete(`/branch-pages/entries/${entryId}`),
    checkInEntry: (entryId, data) => api.post(`/branch-pages/entries/${entryId}/check-in`, data),
    checkOutEntry: (entryId) => api.post(`/branch-pages/entries/${entryId}/check-out`),
};

/**
 * PMS Integration API (CapitaLand ODX WiFi)
 */
export const pmsAPI = {
    verifyWifi:      (data) => api.post('/pms/wifi/verify', data),
    getConfigs:      (params = {}) => api.get('/pms/configs', { params }),
    getConfig:       (branchId) => api.get(`/pms/config/${branchId}`),
    updateConfig:    (branchId, data) => api.put(`/pms/config/${branchId}`, data),
    checkStatus:     (branchId) => api.get(`/pms/status/${branchId}`),
    getSystemDefaults: () => api.get('/pms/system-defaults'),
    // MongoDB activity logs (structured, queryable)
    getActivityLogs: (params = {})              => api.get('/pms/activity-logs', { params }),
    // Server log file reader
    listLogs:   ()                              => api.get('/pms/logs'),
    readLog:    (filename, params = {})         => api.get(`/pms/logs/${filename}`, { params }),
    // WinCloud PMS
    updateWincloudConfig: (branchId, data) => api.put(`/pms/wincloud/${branchId}`, data),
    syncWincloud:         (branchId)       => api.post(`/pms/wincloud/${branchId}/sync`),
    getWincloudRoomGuest: (branchId, roomNo) => api.get(`/pms/wincloud/${branchId}/guest`, { params: { roomNo } }),
    // WinHMS PMS
    updateWinhmsConfig:   (branchId, data) => api.put(`/pms/winhms/${branchId}`, data),
    syncWinhms:           (branchId)       => api.post(`/pms/winhms/${branchId}/sync`),
    getWinhmsRoomGuest:   (branchId, roomNo) => api.get(`/pms/winhms/${branchId}/guest`, { params: { roomNo } }),
    // IDS Next PMS
    updateIdsnextConfig:  (branchId, data) => api.put(`/pms/idsnext/${branchId}`, data),
    syncIdsnext:          (branchId)       => api.post(`/pms/idsnext/${branchId}/sync`),
    getIdsnextRoomGuest:  (branchId, roomNo) => api.get(`/pms/idsnext/${branchId}/guest`, { params: { roomNo } }),
    // HotelKey (Datastream poll) — Sync Now re-queues today's in-house reservations
    updateHotelkeyConfig: (branchId, data) => api.put(`/pms/hotelkey/${branchId}`, data),
    syncHotelkey:         (branchId)       => api.post(`/pms/hotelkey/${branchId}/sync`),
    getHotelkeyRoomGuest: (branchId, roomNo) => api.get(`/pms/hotelkey/${branchId}/guest`, { params: { roomNo } }),
    // InnKey (Wi-Fi Interface API)
    updateInnkeyConfig:   (branchId, data) => api.put(`/pms/innkey/${branchId}`, data),
    syncInnkey:           (branchId)       => api.post(`/pms/innkey/${branchId}/sync`),
    getInnkeyRoomGuest:   (branchId, roomNo) => api.get(`/pms/innkey/${branchId}/guest`, { params: { roomNo } }),
    // HOTLA PMS — has both real-time per-room verify AND a bulk sync
    // (PropNm-only call returns the full guest list)
    updateHotlaConfig:    (branchId, data) => api.put(`/pms/hotla/${branchId}`, data),
    syncHotla:            (branchId)       => api.post(`/pms/hotla/${branchId}/sync`),
    getHotlaRoomGuest:    (branchId, roomNo) => api.get(`/pms/hotla/${branchId}/guest`, { params: { roomNo } }),
    // FIAS — config only (events are pushed to us)
    updateFiasConfig:     (branchId, data) => api.put(`/pms/fias/${branchId}`, data),
    // Per-branch webhook: URL + property code + secret status + recent events.
    // generateWebhookSecret returns the new secret once; it is never readable again.
    getWebhookConfig:      (branchId, provider) => api.get(`/pms/webhook/${branchId}`, { params: { provider } }),
    generateWebhookSecret: (branchId, provider) => api.post(`/pms/webhook/${branchId}/secret`, { provider }),
};

/**
 * System Settings API — global OTP delivery config (WhatsApp/SMS), superadmin
 * only. One shared sender used by every branch across every tenant.
 */
export const systemSettingsAPI = {
    get:     ()     => api.get('/system-settings'),
    update:  (data) => api.put('/system-settings', data),
    testOtp: (data) => api.post('/system-settings/test-otp', data),
};

/**
 * VPN User API — L2TP/IPsec credentials for reaching a router behind CGNAT.
 * Superadmin-only.
 */
export const vpnUserAPI = {
    getAll:  ()             => api.get('/vpn-users'),
    getOne:  (id)           => api.get(`/vpn-users/${id}`),
    create:  (data)         => api.post('/vpn-users', data),
    update:  (id, data)     => api.put(`/vpn-users/${id}`, data),
    delete:  (id, force)    => api.delete(`/vpn-users/${id}`, { params: force ? { force: true } : {} }),
    preview: (id, action)   => api.get(`/vpn-users/${id}/apply/preview`, { params: { action } }),
    apply:   (id, action)   => api.post(`/vpn-users/${id}/apply`, { action }),
};

/**
 * Admin subdomains — a tenant's / branch's own admin address with its branding.
 * branding() is public: what this address's sign-in page shows.
 */
export const adminDomainAPI = {
    branding:  ()         => api.get('/admin-domains/branding'),
    list:      (tenantId) => api.get('/admin-domains', { params: tenantId ? { tenantId } : {} }),
    create:    (data)     => api.post('/admin-domains', data),
    status:    (id)       => api.get(`/admin-domains/${id}/status`),
    provision: (id)       => api.post(`/admin-domains/${id}/provision`),
    remove:    (id)       => api.delete(`/admin-domains/${id}`),
};

/**
 * Dashboard API (Global Dashboard Stats)
 */
export const dashboardAPI = {
    getStats: (params) => api.get('/dashboard/stats', { params }),
    getSuperAdminStats: () => api.get('/dashboard/superadmin'),
    getSystemStatus: () => api.get('/dashboard/system-status'),
};

/**
 * DoT Compliance Logs API (superadmin only)
 */
export const dotLogsAPI = {
    getSyslogStatus: () => api.get('/dot-logs/syslog-status'),
    listDates:   (year, month) => api.get('/dot-logs/dates', { params: { year, month } }),
    listNas:     (date)        => api.get('/dot-logs/nas-list', { params: { date } }),
    getSessions: (params)      => api.get('/dot-logs/sessions', { params }),
    getBrowsing: (params)      => api.get('/dot-logs/browsing', { params }),
    getRouterSystem: (params)  => api.get('/dot-logs/router-system', { params }),
    getSummary:  (date)        => api.get('/dot-logs/summary', { params: { date } }),
    downloadUrl: (params)      => {
        const q = new URLSearchParams(params).toString();
        return `${api.defaults.baseURL}/dot-logs/download?${q}`;
    },
    downloadZipUrl: (date) => `${api.defaults.baseURL}/dot-logs/download-zip?date=${date}`,
    // A day's CSV as a table — the admin panel makes the Excel file from it.
    downloadTable: (params) => api.get('/dot-logs/download', { params: { ...params, format: 'json' } }),
};

/**
 * Centralized Hotspot Logs Hub API — federates Session/SyslogEntry/PmsLog/
 * AuthorizationSession/AuditLog rather than duplicating them.
 */
export const logsAPI = {
    getSummary:         (params) => api.get('/logs', { params }),
    getAuditLogs:       (params) => api.get('/logs/audit', { params }),
    getAuditLog:        (id)     => api.get(`/logs/audit/${id}`),
    getSessions:        (params) => api.get('/logs/sessions', { params }),
    getSessionDetail:   (sessionId) => api.get(`/logs/sessions/${sessionId}`),
    getDeviceLogs:      (mac, params) => api.get(`/logs/devices/${mac}/logs`, { params }),
    getSecurityLogs:    (params) => api.get('/logs/security', { params }),
    getSecurityLog:     (id)     => api.get(`/logs/security/${id}`),
    getPmsLogs:         (params) => api.get('/logs/pms', { params }),
    getAuthenticationLogs: (params) => api.get('/logs/authentication', { params }),
    getDhcpLogs:        (params) => api.get('/logs/dhcp', { params }),
    getNatLogs:         (params) => api.get('/logs/nat', { params }),
    getPreLoginLogs:    (params) => api.get('/logs/pre-login', { params }),
    getGuestActivity:   (params) => api.get('/logs/guest-activity', { params }),
    exportUrl: (logType, params = {}) => {
        const q = new URLSearchParams({ logType, ...params }).toString();
        return `${api.defaults.baseURL}/logs/export?${q}`;
    },
    // The export's table as JSON — the admin panel makes the Excel file from it.
    exportTable: (logType, params = {}) => api.get('/logs/export', { params: { logType, ...params, format: 'json' } }),
};

/**
 * Payment Gateway Config API (Cashfree, Razorpay, PhonePe, Paytm)
 */
export const paymentConfigAPI = {
    getAll: (params) => api.get('/payment-config', { params }),
    getById: (id) => api.get(`/payment-config/${id}`),
    create: (data) => api.post('/payment-config', data),
    update: (id, data) => api.put(`/payment-config/${id}`, data),
    delete: (id) => api.delete(`/payment-config/${id}`),
    getOrders: (params) => api.get('/payment-config/orders/list', { params }),
};

/**
 * User Type Management API (Staff/VIP/Corporate/etc. WiFi guest categories)
 */
export const userTypeAPI = {
    getAll: (params) => api.get('/user-types', { params }),
    getById: (id) => api.get(`/user-types/${id}`),
    create: (data) => api.post('/user-types', data),
    update: (id, data) => api.put(`/user-types/${id}`, data),
    delete: (id) => api.delete(`/user-types/${id}`),
    reapply: (id) => api.post(`/user-types/${id}/reapply`),
};

/**
 * Plan Templates API (reusable, cloneable plan blueprints)
 */
export const planTemplateAPI = {
    getAll: (params) => api.get('/plan-templates', { params }),
    getById: (id) => api.get(`/plan-templates/${id}`),
    create: (data) => api.post('/plan-templates', data),
    update: (id, data) => api.put(`/plan-templates/${id}`, data),
    delete: (id) => api.delete(`/plan-templates/${id}`),
    clone: (id, data) => api.post(`/plan-templates/${id}/clone`, data),
    applyToBranch: (id, data) => api.post(`/plan-templates/${id}/apply`, data),
};

/**
 * Plan History / Audit Trail API
 */
export const planHistoryAPI = {
    getAll: (params) => api.get('/plan-history', { params }),
    getByUser: (userId) => api.get(`/plan-history/user/${userId}`),
};

/**
 * VLAN Profile API
 */
export const vlanProfileAPI = {
    getAll: (params) => api.get('/vlan-profiles', { params }),
    getById: (id) => api.get(`/vlan-profiles/${id}`),
    create: (data) => api.post('/vlan-profiles', data),
    update: (id, data) => api.put(`/vlan-profiles/${id}`, data),
    delete: (id) => api.delete(`/vlan-profiles/${id}`),
};

// Device Status report — infrastructure devices (switches/APs/gateways) tracked
// for online/offline status. Distinct from deviceAPI, which is guest-device
// access control (MAC whitelisting, plan assignment).
export const deviceStatusAPI = {
    getAll: (params) => api.get('/device-status', { params }),
    create: (data) => api.post('/device-status', data),
    importCsv: (file, branchId) => {
        const formData = new FormData();
        formData.append('file', file);
        if (branchId) formData.append('branchId', branchId);
        return api.post('/device-status/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    templateUrl: () => `${api.defaults.baseURL}/device-status/template`,
    bulkDelete: (ids) => api.post('/device-status/bulk-delete', { ids }),
    deleteAll: (branchId) => api.delete('/device-status/all', { params: { branchId } }),
};

// Admin activity / audit trail — every CRUD action already recorded via
// services/auditLog.service.js across the app (NAS, plans, tenants, branches,
// admins, rooms, landing pages, ...).
export const auditLogAPI = {
    getAll: (params) => api.get('/audit-logs', { params }),
};

/**
 * Connected Users — live plan upgrade/downgrade, bulk actions
 */
export const connectedUserAPI = {
    getAll: (params) => api.get('/connected-users', { params }),
    upgrade: (id, data) => api.post(`/connected-users/${id}/upgrade`, data),
    bulkAction: (data) => api.post('/connected-users/bulk-action', data),
};

/**
 * Guest Membership Tiers API
 */
export const membershipTierAPI = {
    getAll: (params) => api.get('/membership-tiers', { params }),
    create: (data) => api.post('/membership-tiers', data),
    update: (id, data) => api.put(`/membership-tiers/${id}`, data),
    delete: (id) => api.delete(`/membership-tiers/${id}`),
};

/**
 * Internet Add-On Store API
 */
export const addonAPI = {
    getAll: (params) => api.get('/addons', { params }),
    create: (data) => api.post('/addons', data),
    update: (id, data) => api.put(`/addons/${id}`, data),
    delete: (id) => api.delete(`/addons/${id}`),
    purchase: (data) => api.post('/addons/purchase', data),
};

/**
 * Plan Scheduler Jobs API
 */
export const schedulerJobAPI = {
    getAll: (params) => api.get('/scheduler-jobs', { params }),
    create: (data) => api.post('/scheduler-jobs', data),
    cancel: (id) => api.post(`/scheduler-jobs/${id}/cancel`),
    delete: (id) => api.delete(`/scheduler-jobs/${id}`),
};

/**
 * Policy Engine — combined-condition plan assignment rules
 */
export const policyRuleAPI = {
    getAll: (params) => api.get('/policy-rules', { params }),
    create: (data) => api.post('/policy-rules', data),
    update: (id, data) => api.put(`/policy-rules/${id}`, data),
    delete: (id) => api.delete(`/policy-rules/${id}`),
};

/**
 * Reports — usage/revenue/plan-sales/user-status + CSV export
 */
export const reportAPI = {
    getUsage: (params) => api.get('/reports/usage', { params }),
    getRevenue: (params) => api.get('/reports/revenue', { params }),
    getPlanSales: (params) => api.get('/reports/plan-sales', { params }),
    getUserStatus: (params) => api.get('/reports/user-status', { params }),
    // CSV export needs the same Authorization header as other requests, so it
    // must go through axios (a plain <a href> link would skip the JWT and 401).
    exportCSV: (type, params) => api.get(`/reports/export/${type}`, { params, responseType: 'blob' }),
    // The export's table as JSON — the admin panel makes the Excel file from it.
    exportTable: (type, params) => api.get(`/reports/export/${type}`, { params: { ...params, format: 'json' } }),
};

/**
 * Outbound Webhooks
 */
export const webhookAPI = {
    getAll: (params) => api.get('/webhooks', { params }),
    create: (data) => api.post('/webhooks', data),
    update: (id, data) => api.put(`/webhooks/${id}`, data),
    delete: (id) => api.delete(`/webhooks/${id}`),
    test: (id) => api.post(`/webhooks/${id}/test`),
};

/**
 * Upgrade Approvals — RBAC approval workflow for branch-admin upgrade requests
 */
export const upgradeApprovalAPI = {
    getAll: (params) => api.get('/upgrade-approvals', { params }),
    decide: (id, decision, note) => api.post(`/upgrade-approvals/${id}/decide`, { decision, note }),
};

export const authSetAPI = {
    getAll: (params) => api.get('/auth-sets', { params }),
    getById: (id) => api.get(`/auth-sets/${id}`),
    create: (data) => api.post('/auth-sets', data),
    update: (id, data) => api.put(`/auth-sets/${id}`, data),
    delete: (id) => api.delete(`/auth-sets/${id}`),
    duplicate: (id) => api.post(`/auth-sets/${id}/duplicate`),
};

export const mikrotikAPI = {
    generateScript:  (nasId)        => api.get(`/mikrotik/script/${nasId}`),
    getConfig:       (nasId)        => api.get(`/mikrotik/config/${nasId}`),
    pushConfig:      (nasId, opts)  => api.post(`/mikrotik/push/${nasId}`, opts || {}),
    saveInterfaces:  (nasId, data)  => api.post(`/mikrotik/interfaces/${nasId}`, data),
    // SSL
    getSslStatus:    (nasId)        => api.get(`/mikrotik/ssl/status/${nasId}`),
    saveSslCert:     (nasId, data)  => api.post(`/mikrotik/ssl/save/${nasId}`, data),
    pushSslCert:     (nasId)        => api.post(`/mikrotik/ssl/push/${nasId}`),
    generateSslCert:  (nasId, data)   => api.post(`/mikrotik/ssl/generate/${nasId}`, data),
    loadCertbotCert:  (nasId, domain) => api.get(`/mikrotik/ssl/certbot-load/${nasId}`, { params: { domain } }),
    // Live active sessions from router
    getActiveUsers:  (nasId)        => api.get(`/mikrotik/active/${nasId}`),
    kickUser:        (nasId, mac)   => api.post(`/mikrotik/active/kick/${nasId}`, { mac }),
};

export const accessControlAPI = {
    getAll:     ()            => api.get('/access-control'),
    getByRole:  (role)        => api.get(`/access-control/role/${role}`),
    getMine:    ()            => api.get('/access-control/me'),
    update:     (role, data)  => api.put(`/access-control/${role}`, data),
    // Roles the superadmin makes, and one administrator's own access
    createRole: (data)        => api.post('/access-control/roles', data),
    deleteRole: (role, force) => api.delete(`/access-control/roles/${role}`, { params: force ? { force: true } : {} }),
    listUsers:  (search)      => api.get('/access-control/users', { params: search ? { search } : {} }),
    updateUser: (id, data)    => api.put(`/access-control/users/${id}`, data),
};

/**
 * Enterprise Syslog Dashboard API (superadmin only)
 */
export const syslogAPI = {
    getDashboard:   (params) => api.get('/syslog/dashboard', { params }),
    getLogs:        (params) => api.get('/syslog/logs', { params }),
    getLive:        ()       => api.get('/syslog/live'),
    getThreats:     (params) => api.get('/syslog/threats', { params }),
    getAlerts:      (params) => api.get('/syslog/alerts', { params }),
    getByCategory:  (cat, params) => api.get(`/syslog/category/${cat}`, { params }),
    getRouterStats: (nasIp, params) => api.get(`/syslog/router/${nasIp}`, { params }),
    acknowledgeAlert: (id)   => api.put(`/syslog/alerts/${id}/acknowledge`),
    resolveAlert:   (id)     => api.put(`/syslog/alerts/${id}/resolve`),
    exportUrl:      (params) => {
        const q = new URLSearchParams(params).toString();
        return `${api.defaults.baseURL}/syslog/export?${q}`;
    },
    // The export's table as JSON — the admin panel makes the Excel file from it.
    exportTable:    (params) => api.get('/syslog/export', { params: { ...params, format: 'json' } }),
};

export default api;
